import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, Info, AlertCircle } from 'lucide-react';

export type ToastTone = 'success' | 'error' | 'info';

/** The pill itself: bottom-centre, --t1 on --bg, rising in. */
export function ToastPill({ message, tone = 'success' }: { message: ReactNode; tone?: ToastTone }) {
  const Icon = tone === 'error' ? AlertCircle : tone === 'info' ? Info : Check;
  return createPortal(
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      className="fixed left-1/2 bottom-7 z-[95] flex items-center gap-2.5 px-[18px] py-3 rounded-full bg-t1 text-bg text-[14px] font-medium shadow-wp text-center"
      style={{ transform: 'translateX(-50%)', maxWidth: 'calc(100vw - 32px)' }}
    >
      <span className="flex items-center gap-2.5" style={{ animation: 'wpRise .35s var(--ease) both' }}>
        <Icon size={16} strokeWidth={2.4} aria-hidden="true" className="shrink-0" />
        <span>{message}</span>
      </span>
    </div>,
    document.body,
  );
}

interface ToastState {
  id: number;
  message: ReactNode;
  tone: ToastTone;
}

const ToastContext = createContext<((message: ReactNode, tone?: ToastTone) => void) | null>(null);

/** One toast at a time; a new one replaces the current one. */
export function ToastProvider({ children, duration = 2600 }: { children: ReactNode; duration?: number }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const seq = useRef(0);

  const show = useCallback(
    (message: ReactNode, tone: ToastTone = 'success') => {
      clearTimeout(timer.current);
      const id = ++seq.current;
      setToast({ id, message, tone });
      timer.current = setTimeout(() => setToast((t) => (t?.id === id ? null : t)), duration);
    },
    [duration],
  );

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {toast && <ToastPill key={toast.id} message={toast.message} tone={toast.tone} />}
    </ToastContext.Provider>
  );
}

/** Returns `toast(message, tone?)`. Falls back to a no-op outside a provider (tests). */
export function useToast() {
  return useContext(ToastContext) ?? noop;
}
const noop = () => {};
